import type { Question, QuestionType, SelectionLimit } from './contract'

// 題型的行為表。純邏輯、不 import lit —— 模板（哪一種畫面）在 render/question.ts 的
// QUESTION_BODIES，那張表也以 QuestionType 為 key，兩張表一起窮盡。
//
// 這裡持有的是行為：答案是什麼形狀、能不能複選、送出時落在哪個欄位、非空答案要通過什麼格式、
// 擋下時說哪句話。呼叫端應該向這裡要答案而不是自己比對題型字串；`rg "\.type === '" src/`
// 找得出還在自己比的地方，數字愈接近零愈好。
//
// ⚠️ Record<QuestionType, …> 守得住「每一種題型都要有一列」，守不住「每一列都填滿了」——
// format 是選填的（text 本來就沒有格式規則），所以新增題型時忘了給 pattern 與文案，
// type-check 不會叫，那一題就從此不驗格式。

/**
 * 一種題型的行為。以 `answer` 分岔：選項題與自由填答題連「有哪些欄位」都不一樣，
 * 攤平成一個扁平物件會讓選項題帶著恆為 undefined 的格式規則。
 */
export type QuestionSpec
  = | {
    /** 讀者從 options 裡挑。渲染每個選項，送出走 selected；沒有 options 陣列的題目視為壞題。 */
    answer: 'options'
    /** 可否複選：決定指示器畫勾還是圓點，也決定 selection_limit 有沒有意義。 */
    multi: boolean
    /**
     * 這個題型的「其他」選項（`Option.is_other`）會不會長出自由填答輸入框。單選／複選的選項版型
     * 有空間放（見 render/question.ts 的 `.opt--other`）；dropdown 走自繪選單版型，沒有地方放
     * 輸入框 —— 若驗證層仍認 dropdown 的 `is_other`，讀者選中它會被要求填一個畫面上不存在的欄位，
     * 必填題就此永遠過不了關。
     */
    otherInput: boolean
  }
  | {
    /** 讀者自己打字。 */
    answer: 'text'
    /**
     * 答案落在 SurveyResponse 的哪個欄位。送出時宣告的 `question_type` 一律是題目自己的型別，
     * 不是這個值 —— 型別由後端拿 question_id 去驗才算數，前端送的欄位本來就不該被當成真相。
     */
    field: 'text' | 'date'
    /** 非空答案必須通過的格式，與擋下時說的那句話同源：加規則就得同時給文案。 */
    format?: { pattern: RegExp, hint: string }
  }

// Record<QuestionType, …> 是這張表的守門員：contract.ts 的聯集加一種而這裡沒跟上，
// type-check 就會失敗。反過來也一樣 —— 表裡多一個 key 同樣編不過。
const SPECS: Record<QuestionType, QuestionSpec> = {
  single_choice: { answer: 'options', multi: false, otherInput: true },
  multiple_choice: { answer: 'options', multi: true, otherInput: true },
  scalar: { answer: 'options', multi: false, otherInput: false },
  dropdown: { answer: 'options', multi: false, otherInput: false },
  text: { answer: 'text', field: 'text' },
  // email 與 phone 的答案落在 text 欄位，但送出時仍宣告自己的 question_type。
  // ⚠️ survey API 的 POST enum 今天不含這兩種，所以後端要放行
  // 這兩種題型時，GET 與 POST 必須一起放 —— 只放 GET 的話這兩題會讓整份送出被擋成 400。
  // 見 docs/survey-api-contract-gaps.md 的 P2。
  //
  // 兩條 pattern 都不追求完整規格，只擋掉明顯不是那個東西的輸入。email 的各段刻意互斥
  // （網域片段排除 `.`）：`\S+\.\S+` 那種寫法兩側都吃得下 `.`，惡意輸入可以把它拖成指數回溯
  // （eslint regexp/no-super-linear-backtracking 會擋）。
  email: { answer: 'text', field: 'text', format: { pattern: /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/, hint: '請輸入有效的 email' } },
  // 電話允許數字與 +-() 空白，且至少要有一個數字（lookahead 那半）；不驗國碼或位數，各地區差異太大。
  phone: { answer: 'text', field: 'text', format: { pattern: /^(?=.*\d)[\d+\-()\s]+$/, hint: '請輸入有效的電話號碼' } },
  date: { answer: 'text', field: 'date', format: { pattern: /^\d{4}-\d{2}-\d{2}$/, hint: '請輸入正確的日期格式' } },
}

// 這個 widget 認得的題型，就是表上有列的那些。導出 key 而不是另外寫一份清單：另寫一份的話，
// 兩份會各自漂移，而「認得哪些題型」這件事就有兩個答案。契約對帳測試讀這裡跟後端 enum 比對。
export const QUESTION_TYPES = Object.keys(SPECS) as QuestionType[]

// wire 上的字串是不是我們認得的題型。api.ts 用它守 API 邊界；收窄成 QuestionType 之後
// 後續查表不必再 cast —— 少一個 cast 就少一條繞過這張表的路。
export function isQuestionType(x: unknown): x is QuestionType {
  return typeof x === 'string' && Object.hasOwn(SPECS, x)
}

/** 自由填答題（答案是打出來的字，不是挑出來的選項）。 */
export function isTextQuestion(q: Pick<Question, 'type'>): boolean {
  return SPECS[q.type].answer === 'text'
}

/** 選項題（渲染 options、送出走 selected）。與 isTextQuestion 是同一個劃分的兩半。 */
export function isOptionQuestion(type: QuestionType): boolean {
  return SPECS[type].answer === 'options'
}

/** 可複選：指示器畫反白勾而非圓點，標題掛「可複選」註記，且 selection_limit 有意義。 */
export function isMultiSelect(type: QuestionType): boolean {
  const spec = SPECS[type]
  return spec.answer === 'options' && spec.multi
}

/**
 * 這一題的「其他」選項選中時會不會長出自由填答輸入框——渲染層（要不要畫 `.opt-other-input`）
 * 與驗證層（`is_other` 選中算不算缺自由填答、送出要不要帶 text）共讀同一個答案，不各自比對
 * 題型字串，否則兩層漂移正是這次要修的缺陷（dropdown 帶 is_other 卻無處可填、必填題永遠過不了）。
 */
export function rendersOtherInput(q: Pick<Question, 'type'>): boolean {
  const spec = SPECS[q.type]
  return spec.answer === 'options' && spec.otherInput
}

/** 送出時答案落在哪個欄位；選項題回 undefined（它們走 selected）。 */
export function submitField(type: QuestionType): 'text' | 'date' | undefined {
  const spec = SPECS[type]
  return spec.answer === 'text' ? spec.field : undefined
}

// 選取數量限制的唯一解析入口：渲染、勾選、驗證、送出、prefill 都只讀這裡的結果，不各自判斷 mode。
// 只對可複選的題型有意義；缺值、null、非物件、mode 不認得、count 不是正整數一律回 null =
// 不限（不把使用者鎖死在一條讀不懂的規則裡；呼應 paging.ts「malformed 一律回退到不炸掉的預設
// 值」的寫法，只是這裡的安全預設是「不設限」而非固定數字）。message 只在 trim 後非空時保留，
// 且存的是 trim 後的字串。
export function resolveSelectionLimit(q: Pick<Question, 'type' | 'selection_limit'>): SelectionLimit | null {
  if (!isMultiSelect(q.type))
    return null
  const raw: unknown = q.selection_limit
  if (typeof raw !== 'object' || raw === null)
    return null
  const { mode, count, message } = raw as Partial<Record<keyof SelectionLimit, unknown>>
  if (mode !== 'at_least' && mode !== 'at_most' && mode !== 'exactly')
    return null
  if (typeof count !== 'number' || !Number.isInteger(count) || count < 1)
    return null
  const text = typeof message === 'string' ? message.trim() : ''
  return { mode, count, ...(text.length > 0 ? { message: text } : {}) }
}

// 勾選上限：at_most 與 exactly 勾到 count 就不能再勾，其餘（含不限、at_least）沒有上限。
export function selectionCap(limit: SelectionLimit | null): number | null {
  return limit !== null && (limit.mode === 'at_most' || limit.mode === 'exactly') ? limit.count : null
}

// 已選數量（去重後）是否違反限制。空的作答視為略過，不套限制 —— 必填與否由 answerGap 的
// 'required' 把關，這裡只管「有勾但數量不對」。
export function violatesSelectionLimit(limit: SelectionLimit | null, pickedCount: number): boolean {
  if (limit === null || pickedCount === 0)
    return false
  switch (limit.mode) {
    case 'at_least': return pickedCount < limit.count
    case 'at_most': return pickedCount > limit.count
    case 'exactly': return pickedCount !== limit.count
  }
}

/**
 * 存下來的舊答案，能不能套用到現在這一題（見 prefill.ts）。
 *
 * 自由填答題只看答案落在同一個欄位：一題 email 在後端放行前是以 `text` 送來的，讀者當時存下的
 * 就是 `{ type: 'text' }`，嚴格比對型別會讓那筆在題型改名的當下靜靜消失。放寬的只有型別 ——
 * 值本身仍要通過現在這個題型的格式檢查，所以一個當年隨手填的非 email 字串不會被種回去。
 *
 * 選項題維持嚴格相等：seq 的意思隨可否複選而不同，一份複選的舊答案套進單選題會種出多個選取。
 */
export function storedAnswerFits(stored: unknown, current: QuestionType): boolean {
  if (!isQuestionType(stored))
    return false
  if (stored === current)
    return true
  const a = SPECS[stored]
  const b = SPECS[current]
  return a.answer === 'text' && b.answer === 'text' && a.field === b.field
}

/**
 * 非空答案是否通過**該題型**的格式規則。沒有格式規則的題型一律通過。
 *
 * ⚠️ 要驗讀者當下打在畫面上的值，走 question-precision.ts 的 answerFormatValid，不是這一支
 * —— 有些 date 題只問年份，畫面上放的是四位數，拿完整日期的 pattern 去驗會把每一筆都判成
 * 格式錯誤。這一支認的是題型，適用的是已經序列化成 wire 形狀的值（prefill 讀回 localStorage
 * 那條路）。
 */
export function textFormatValid(type: QuestionType, text: string): boolean {
  const spec = SPECS[type]
  if (spec.answer !== 'text' || !spec.format)
    return true
  return spec.format.pattern.test(text)
}

// 格式不符時要說的那句話。沒有格式規則的題型 textFormatValid 恆真，所以走不到這裡；
// 這個預設值接住的是「新增了格式規則卻忘了給文案」—— 那時讀者看到的是一句通用的話，
// 而不是一段空白。
export function textFormatHint(type: QuestionType): string {
  const spec = SPECS[type]
  return (spec.answer === 'text' && spec.format?.hint) || '格式不正確'
}
